import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import LinearProgress from "@mui/material/LinearProgress";
import EditFieldDialog from "../../ui/dialogs/EditFieldDialog";
import { updateAor } from "../../../services/aorService";
import { store } from "../../../store";
import { getOrder } from "../../../redux/actions/order";
import { NoRowOverlay } from "../../table/NoRowOverlay";
import UserInfo from "../../ui/UserInfo";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import dayjs from "dayjs";
import { getShipmentById } from "../../../services/shipmentService";
import { getAORsByShipmentId } from "../../../services/aorService";
import { fetchProductionsByOrderId } from "../../../services/productionService";
import { useTranslation } from "react-i18next";
import { updateShipment } from "../../../services/shipmentService";
import {
  closeLoadingAlert,
  showErrorAlert,
  showLoadingAlert,
  showSuccessAlert,
} from "../../ui/utils/AlertUtils";
import EditAorDialog from "../../ui/dialogs/EditAorDialog";
import { printAor } from "../../ui/PrintFunctions";
import OrderPageHeader from "./order/shared/OrderPageHeader";
import { MaterialOrderHeader } from "../../ui/BackorderControl";
import { getBackorderDisplayValue } from "./order/shared/BackorderHelper";
import {
  MaterialOrderGridGroup,
  materialOrderGridListSx,
} from "../../ui/MaterialOrderGridLayout";

export const ScreenAor = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const orderId = location.state.orderId;
  const studyId = location.state.studyId;
  const centerId = location.state.centerId;
  const centerNumber = location.state.centerNumber;
  const studyCode = location.state.studyCode;
  const user = useSelector((state) => state.user.user);
  const adminUser = user.role === "admin";
  const order = useSelector((state) => state.order.order);
  const [isLoading, setIsLoading] = React.useState(true);
  const [shipment, setShipment] = React.useState(null);
  const [shippedProductions, setShippedProductions] = React.useState([]);
  const [AORs, setAORs] = React.useState([]);
  const [editedDate, setEditedDate] = React.useState("");
  const [editDateDialogOpen, setEditDateDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [selectedAor, setSelectedAor] = React.useState(null);
  const [editedQuantityReceived, setEditedQuantityReceived] = React.useState(0);
  const [editedMaterialConform, setEditedMaterialConform] =
    React.useState(false);
  const [editedComment, setEditedComment] = React.useState("");
  const shipmentIdFromState = Number(location.state?.shipmentId);
  const handleBackToOrder = () => {
    navigate("/order", {
      state: {
        orderId,
        order,
        centerNumber,
        studyCode,
        studyId,
        centerId,
      },
    });
  };
  const handleDownloadAor = () => {
    printAor(order, shipment, centerNumber, AORs, shippedProductions);
  };

  const [materialsTypeMaterial, setMaterialsTypeMaterial] = React.useState(
    order?.Supplies.filter((supply) => supply.Material.type === "material"),
  );
  const [materialsTypeSupply, setMaterialsTypeSupply] = React.useState(
    order?.Supplies.filter((supply) => supply.Material.type === "supply"),
  );
  const [materialsTypeReagent, setMaterialsTypeReagent] = React.useState(
    order?.Supplies.filter((supply) => supply.Material.type === "reagent"),
  );
  const [materialsTypeDocuments, setMaterialsTypeDocuments] = React.useState(
    order?.Supplies.filter((supply) => supply.Material.type === "document"),
  );

  const handleSaveAor = async () => {
    if (!editedMaterialConform && !editedComment.trim()) {
      showErrorAlert(t("commentMandatory"));
      return;
    }
    const updatedComment = editedMaterialConform ? "" : editedComment;

    try {
      showLoadingAlert(t("savingChanges"));

      await updateAor(selectedAor.id, {
        quantityReceived: editedQuantityReceived,
        materialConform: editedMaterialConform,
        comment: updatedComment,
      });

      setEditDialogOpen(false);

      const updatedAors = await getAORsByShipmentId(shipment.id);
      setAORs(updatedAors);

      closeLoadingAlert();
      showSuccessAlert(t("changesSaved"));
    } catch (error) {
      closeLoadingAlert();
      showErrorAlert(t("errorSavingChanges"));
      console.error("Error saving AOR:", error);
    }
  };

  const CustomDataGrid = ({ rows, headerName }) => {
    const columns = [
      {
        field: "materialName",
        headerName: headerName,
        flex: 0.4,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.Material.name,
      },
      {
        field: "shippingQuantity",
        headerName: t("quantitySent"),
        flex: 0.1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const productionsWithSameMaterial =
            shipment?.productions?.filter(
              (production) => production.materialId === params.row.Material.id,
            ) || [];
          return <div>{productionsWithSameMaterial?.length}</div>;
        },
        renderHeader: (params) => {
          return (
            <div
              style={{
                lineHeight: "1.5",
                whiteSpace: "normal",
                padding: "5px",
              }}>
              {t("quantitySent")}
            </div>
          );
        },
      },
      {
        field: "quantityReceived",
        headerName: t("quantityReceived"),
        flex: 0.1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const matchingAor = AORs.find(
            (aor) => aor.materialId === params.row.Material.id,
          );
          return <div>{matchingAor?.quantityReceived}</div>;
        },
        renderHeader: (params) => {
          return (
            <div
              style={{
                lineHeight: "1.5",
                whiteSpace: "normal",
                padding: "5px",
              }}>
              {t("quantityReceived")}
            </div>
          );
        },
      },
      {
        field: "materialConform",
        headerName: t("materialConform"),
        flex: 0.1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const matchingAor = AORs.find(
            (aor) => aor.materialId === params.row.Material.id,
          );
          return <Checkbox checked={matchingAor?.materialConform} disabled />;
        },
        renderHeader: (params) => {
          return (
            <div
              style={{
                lineHeight: "1.5",
                whiteSpace: "normal",
              }}>
              {t("materialConform")}
            </div>
          );
        },
      },
      {
        field: "comment",
        headerName: t("describeError"),
        flex: 0.2,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const matchingAor = AORs.find(
            (aor) => aor.materialId === params.row.Material.id,
          );
          return <div>{matchingAor?.comment}</div>;
        },
      },
      {
        field: "backorder",
        headerName: t("backorder"),
        flex: 0.1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div>
            {getBackorderDisplayValue(
              params.row,
              shippedProductions,
              t("backorderComplete"),
            )}
          </div>
        ),
        renderHeader: () => {
          return (
            <div
              style={{
                lineHeight: "1.5",
                whiteSpace: "normal",
              }}>
              {t("backorder")}
            </div>
          );
        },
      },
    ];

    if (!rows || rows.length === 0) {
      return null;
    }

    return (
      <MaterialOrderGridGroup>
        <DataGrid
          slots={{
            noRowsOverlay: NoRowOverlay,
            loadingOverlay: LinearProgress,
          }}
          rows={rows}
          columns={columns}
          onRowDoubleClick={(params) => {
            if (!adminUser) {
              return;
            }
            const matchingAor = AORs.find(
              (aor) => aor.materialId === params.row.Material.id,
            );
            if (matchingAor) {
              setSelectedAor(matchingAor);
              setEditedQuantityReceived(matchingAor.quantityReceived || 0);
              setEditedMaterialConform(matchingAor.materialConform || false);
              setEditedComment(matchingAor.comment || "");
              setEditDialogOpen(true);
            }
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 30 },
            },
          }}
          rowHeight={40}
          pageSizeOptions={[30, 50]}
          disableSelectionOnClick
          hideFooter
          autoHeight
          hideScrollbar
        />
      </MaterialOrderGridGroup>
    );
  };

  React.useEffect(() => {
    let resolvedShipmentId = shipmentIdFromState;

    if (Number.isNaN(resolvedShipmentId) || resolvedShipmentId <= 0) {
      resolvedShipmentId = Number(
        (Array.isArray(order?.shipments)
          ? order.shipments.find(
              (candidateShipment) =>
                candidateShipment?.confirmedDeliveryDate == null ||
                candidateShipment?.confirmedDeliveryDate === false,
            )?.id
          : null) || null,
      );
    }

    if (Number.isNaN(resolvedShipmentId) || resolvedShipmentId <= 0) {
      return;
    }

    getShipmentById(resolvedShipmentId).then((resolvedShipment) => {
      setShipment(resolvedShipment);
    });
  }, [order?.shipments, shipmentIdFromState]);

  React.useEffect(() => {
    if (shipment) {
      getAORsByShipmentId(shipment.id).then((aors) => {
        if (aors) {
          setAORs(aors);
        }
      });
    }
  }, [shipment]);

  React.useEffect(() => {
    if (!orderId) {
      setShippedProductions([]);
      return;
    }

    let shouldSetState = true;

    fetchProductionsByOrderId(orderId)
      .then((productions) => {
        if (shouldSetState) {
          setShippedProductions(Array.isArray(productions) ? productions : []);
        }
      })
      .catch((error) => {
        console.error("Error loading shipped productions:", error);
        if (shouldSetState) {
          setShippedProductions([]);
        }
      });

    return () => {
      shouldSetState = false;
    };
  }, [orderId]);

  // fetch order
  React.useEffect(() => {
    store.dispatch(getOrder(orderId)).then(() => setIsLoading(false));
  }, [orderId]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
        }}>
        <OrderPageHeader
          title={`Order ${order?.orderNumber || ""}`}
          downloadLabel="Download AOR"
          onBack={handleBackToOrder}
          onDownload={handleDownloadAor}
          downloadDisabled={!order || !shipment || AORs.length === 0}
        />
        <Box sx={{ p: 2 }}>
          <Box sx={materialOrderGridListSx}>
            <TextField
              value={t("acknowledgementOfReceipt")}
              InputProps={{
                readOnly: true,
              }}
              inputProps={{
                style: { textAlign: "center", fontWeight: "bold" },
              }}
              sx={{ marginBottom: 2 }}
              fullWidth
            />
            <MaterialOrderHeader
              backorderLabel={t("onBackorder")}
              showBackorder={Boolean(shipment?.onBackorder)}
              title={t("materialOrder").toUpperCase()}
            />
            <CustomDataGrid
              rows={materialsTypeMaterial}
              setRows={setMaterialsTypeMaterial}
              headerName={t("material")}
            />
            <CustomDataGrid
              rows={materialsTypeSupply}
              setRows={setMaterialsTypeSupply}
              headerName={t("supply")}
            />
            <CustomDataGrid
              rows={materialsTypeReagent}
              setRows={setMaterialsTypeReagent}
              headerName={t("reagent")}
            />
            <CustomDataGrid
              rows={materialsTypeDocuments}
              setRows={setMaterialsTypeDocuments}
              headerName={t("document")}
            />
          </Box>
          <UserInfo
            title={t("personInChargeOfReception").toUpperCase()}
            user={shipment?.receivedBy}
            date={dayjs(shipment?.confirmedDeliveryDate).format("DD/MM/YYYY")}
            onDoubleClick={() => {
              if (adminUser) {
                setEditedDate(
                  shipment?.confirmedDeliveryDate?.split("T")[0] || "",
                );
                setEditDateDialogOpen(true);
              }
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={handleBackToOrder}
            style={{ padding: "16px 88px", margin: "16px" }}>
            {t("backToOrder")}
          </Button>
        </Box>
        <EditAorDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          quantityReceived={editedQuantityReceived}
          setQuantityReceived={setEditedQuantityReceived}
          materialConform={editedMaterialConform}
          setMaterialConform={setEditedMaterialConform}
          comment={editedComment}
          setComment={setEditedComment}
          onSave={handleSaveAor}
        />
        <EditFieldDialog
          open={editDateDialogOpen}
          onClose={() => setEditDateDialogOpen(false)}
          field="confirmedDeliveryDate"
          label={t("confirmedDeliveryDate")}
          value={editedDate}
          onSave={async (field, newValue) => {
            if (shipment && shipment.id) {
              await updateShipment(shipment.id, field, newValue);
              setEditedDate(newValue);
              showSuccessAlert(t("shipmentUpdated"));

              // Re-fetch the updated shipment
              const updatedShipment = await getShipmentById(shipment.id);
              setShipment(updatedShipment);
            }
            setEditDateDialogOpen(false);
          }}
        />
      </Paper>
    </Grid>
  );
};
export default ScreenAor;
